import { useState } from "react";
import { API_URL } from "../config/endpoints"; // Ensure correct endpoint structure

export type DoorReservationAction = "lock" | "unlock";

type DoorActionFailure = {
  status?: string;
  reason?: string;
  message?: string;
};

const getDoorActionMessage = (action: DoorReservationAction) =>
  action === "lock"
    ? "We couldn't lock the door right now. Please try again."
    : "We couldn't unlock the door right now. Please try again.";

const getDoorActionFailureMessage = (
  action: DoorReservationAction,
  reason?: string,
  message?: string
) => {
  switch (reason) {
    case "remote_lock_not_supported":
      return "Remote lock is not supported for this door.";
    case "remote_unlock_not_supported":
      return "Remote unlock is not supported for this door.";
    case "reservation_outside_active_window":
      return "This door action is only available during the active reservation window.";
    case "door_not_mapped_to_reservation":
      return "This door is not linked to your reservation.";
    case "device_is_not_door":
      return "This device is not a door lock.";
    case "provider_command_failed":
      return "The lock provider could not complete the request. Please try again.";
    default:
      return message || getDoorActionMessage(action);
  }
};

const readDoorActionResponse = async (
  response: Response
): Promise<DoorActionFailure | null> => {
  try {
    return await response.json();
  } catch (error) {
    return null;
  }
};

export const useUnlockUnit = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [activeLockUid, setActiveLockUid] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [unlockedLocks, setUnlockedLocks] = useState<Record<string, boolean>>({});

  const setDoorReservation = async (
    lockUid: string,
    reservationUid: string,
    action: DoorReservationAction
  ) => {
    if (!lockUid || !reservationUid) {
      setErrorMessage("Lock UID or Reservation UID is missing.");
      return;
    }

    const apiUrl = `${API_URL}/guest/iot/doors/${lockUid}/set_reservation`;

    setLoading(true);
    setActiveLockUid(lockUid);
    setErrorMessage(null);

    try {
      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("Authentication token is missing. Please log in.");
      }

      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ reservationId: reservationUid, action }),
      });

      const responseData = await readDoorActionResponse(response);

      if (!response.ok || responseData?.status === "error") {
        const userFriendlyMessage = getDoorActionFailureMessage(
          action,
          responseData?.reason,
          responseData?.message
        );

        console.warn("Guest door action failed", {
          action,
          lockUid,
          reservationUid,
          httpStatus: response.status,
          reason: responseData?.reason,
          response: responseData,
        });

        setErrorMessage(userFriendlyMessage);
        throw new Error(userFriendlyMessage);
      }

      setUnlockedLocks((currentLocks) => ({
        ...currentLocks,
        [lockUid]: action === "unlock",
      }));
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : "An unexpected error occurred."
      );
      throw error; // Re-throw the error so it propagates to the component
    } finally {
      setLoading(false);
      setActiveLockUid(null);
    }
  };

  const unlockDoor = (lockUid: string, reservationUid: string) =>
    setDoorReservation(lockUid, reservationUid, "unlock");

  const lockDoor = (lockUid: string, reservationUid: string) =>
    setDoorReservation(lockUid, reservationUid, "lock");

  const isDoorUnlocked = (lockUid?: string | null) =>
    Boolean(lockUid && unlockedLocks[lockUid]);

  return {
    loading,
    activeLockUid,
    errorMessage,
    isUnlocked: Object.values(unlockedLocks).some(Boolean),
    isDoorUnlocked,
    setDoorReservation,
    unlockDoor,
    lockDoor,
  };
};
